import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-wt-bg-primary text-wt-text-primary">
      <header className="sticky top-0 z-50 bg-wt-bg-primary/95 backdrop-blur-sm border-b border-wt-border-default">
        <div className="mx-auto max-w-4xl px-4 py-4 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <Logo variant="fullTransparent" size="h-8" />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12 lg:px-8">
        <h1 className="mb-2 font-sans text-3xl font-bold md:text-4xl">
          Watt Tower — Terms of Service
        </h1>
        <p className="mb-12 font-sans text-sm text-wt-text-muted">
          Effective Date: February 1, 2026
        </p>

        <article className="space-y-10 font-sans text-base leading-relaxed text-wt-text-secondary">
          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              1. Acceptance of These Terms
            </h2>
            <p className="mb-4">
              By accessing or using the Watt Tower application and services (“Watt Tower,” “Service,” “we,” “our,” or “us”), you agree to be bound by these Terms of Service (“Terms”).
            </p>
            <p className="mb-4">
              If you do not agree to these Terms, you may not use the Service.
            </p>
            <p>
              These Terms apply to all users, accounts, and monitored properties.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              2. Description of the Service
            </h2>
            <p className="mb-4">
              Watt Tower provides monitoring, analytics, forecasting, and advisory insights related to solar, battery, and hybrid energy systems.
            </p>
            <p className="mb-4">The Service may include:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Energy telemetry visualization</li>
              <li>Historical and real-time analytics</li>
              <li>Forecasts and projections</li>
              <li>Informational alerts and notifications</li>
              <li>System status summaries</li>
            </ul>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">Important Clarifications</h3>
            <p className="mb-4">
              Watt Tower: does not control energy systems; does not issue commands to inverters, batteries, generators, or loads; does not guarantee system performance, uptime, or energy availability; does not replace operator judgment or local system controls.
            </p>
            <p>All insights, forecasts, and alerts are informational only.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              3. Eligibility & Account Responsibility
            </h2>
            <p className="mb-4">You represent and warrant that:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>You are at least 18 years old</li>
              <li>You have legal authority to monitor the systems connected to your account</li>
              <li>Information you provide is accurate and up to date</li>
            </ul>
            <p className="mb-4">You are responsible for:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Maintaining the security of your account credentials</li>
              <li>Ensuring correct system configuration</li>
              <li>Providing and maintaining internet connectivity</li>
              <li>Managing hardware, firmware, and inverter credentials</li>
              <li>All activity that occurs under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              4. User Responsibilities & Operational Control
            </h2>
            <p className="mb-4">You acknowledge and agree that:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>You retain sole responsibility for operating your energy systems</li>
              <li>You are responsible for load management, generator operation, and safety decisions</li>
              <li>Watt Tower does not monitor for emergencies or safety conditions</li>
              <li>Watt Tower does not provide real-time guarantees or fail-safe monitoring</li>
            </ul>
            <p className="mb-4">You must not rely on Watt Tower as:</p>
            <ul className="ml-6 list-disc space-y-2">
              <li>An emergency notification system</li>
              <li>A safety monitoring system</li>
              <li>A replacement for local alarms, controllers, or hardware safeguards</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              5. Forecasts, Alerts, and Accuracy
            </h2>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">A. Forecasts & Projections</h3>
            <p className="mb-4">
              Forecasts provided by Watt Tower: are probabilistic and estimate-based; depend on historical data, system configuration, and external inputs; degrade in accuracy over time; may be inaccurate, incomplete, delayed, or unavailable. Actual system behavior may differ materially from projections.
            </p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">B. Alerts & Notifications</h3>
            <p className="mb-4">
              Alerts and notifications: are informational only; may be delayed, interrupted, duplicated, or not delivered; depend on third-party networks, devices, and connectivity. Notification preferences are governed by Section 6 below.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              6. Messaging & Notification Terms
            </h2>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">A. Types of Messages</h3>
            <p className="mb-4">
              Watt Tower sends messages for the following purposes only: system alerts related to energy conditions; energy status notifications; account and service-related messages. We do not send marketing or promotional messages via SMS.
            </p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">B. SMS Opt-In & Consent (Double Opt-In)</h3>
            <p className="mb-4">
              SMS notifications are disabled by default. If you enable SMS notifications, you must explicitly confirm via SMS. After enabling SMS notifications, you will receive:
            </p>
            <blockquote className="mb-4 border-l-4 border-wt-border-default pl-4 font-sans text-sm italic text-wt-text-muted">
              Watt Tower Alerts — You requested SMS notifications for system alerts and account updates. Reply YES to confirm and continue receiving SMS notifications. If you do not reply, no messages will be sent. Msg freq varies. Msg & data rates may apply. Reply STOP to opt out, HELP for support.
            </blockquote>
            <p className="mb-4">You must reply YES to receive SMS notifications.</p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">C. Confirmation & Opt-Out</h3>
            <p className="mb-4">
              After confirmation, you may opt out at any time by replying STOP. Opting out: immediately disables SMS notifications; sends a confirmation message; does not affect in-app or email notifications.
            </p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">D. Message Frequency & Fees</h3>
            <p className="mb-4">
              Message frequency varies based on system conditions and account activity. Standard message and data rates may apply. Watt Tower does not charge separately for SMS messages.
            </p>
            <h3 className="mb-2 font-sans text-lg font-semibold text-wt-text-primary">E. Help & Support</h3>
            <p>
              For assistance: reply HELP to any message, or contact support@watttower.com
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              7. Acceptable Use & System Boundaries
            </h2>
            <p className="mb-4">You may use Watt Tower solely for:</p>
            <ul className="mb-4 ml-6 list-disc space-y-2">
              <li>Monitoring energy telemetry</li>
              <li>Viewing analytics, forecasts, and historical data</li>
              <li>Receiving informational alerts</li>
              <li>Managing account and notification preferences</li>
            </ul>
            <p className="mb-4">You must not use Watt Tower:</p>
            <p className="mb-2 font-semibold text-wt-text-primary">A. For Safety-Critical or Life-Support Systems</p>
            <p className="mb-4">Including medical, emergency response, fire detection, or life-support systems.</p>
            <p className="mb-2 font-semibold text-wt-text-primary">B. For Automated Control or Decision-Making</p>
            <p className="mb-4">You must not use Watt Tower outputs as inputs to automated control logic or actuation.</p>
            <p className="mb-2 font-semibold text-wt-text-primary">C. For Regulatory or Compliance Determinations</p>
            <p className="mb-4">Including safety certifications, financial, tax, insurance, or grid compliance.</p>
            <p className="mb-2 font-semibold text-wt-text-primary">D. For Unauthorized Access or Misuse</p>
            <p>Including monitoring systems you do not control, bypassing safeguards, or interfering with service operation.</p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              8. No Emergency or Life-Safety Use
            </h2>
            <p>
              Watt Tower is not designed or intended for emergency, safety-critical, or life-support use. You agree not to rely on the Service in any context where failure or delay could result in injury, death, or property damage.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              9. Fees & Subscriptions
            </h2>
            <p className="mb-4">
              Certain features may require a paid subscription. Unless otherwise stated: fees are billed in advance; subscription fees are non-refundable; you are responsible for applicable taxes; pricing and plans may change with notice.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              10. Intellectual Property
            </h2>
            <p>
              All rights, title, and interest in the Service are owned by Watt Tower or its licensors. You are granted a limited, non-exclusive, non-transferable license to use the Service in accordance with these Terms.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              11. Disclaimer of Warranties
            </h2>
            <p className="mb-4">
              THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WATT TOWER DISCLAIMS ALL WARRANTIES, INCLUDING: MERCHANTABILITY; FITNESS FOR A PARTICULAR PURPOSE; ACCURACY OR RELIABILITY OF DATA; UNINTERRUPTED OR ERROR-FREE OPERATION.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              12. Limitation of Liability
            </h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW: WATT TOWER SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR SPECIAL DAMAGES. THIS INCLUDES LOSS OF POWER, BUSINESS INTERRUPTION, DATA LOSS, OR EQUIPMENT DAMAGE. WATT TOWER’S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              13. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless Watt Tower from claims arising out of: your use of the Service; your system configuration or operation; your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              14. Privacy
            </h2>
            <p>
              Your use of the Service is also governed by the{" "}
              <Link to="/privacy" className="text-wt-text-secondary underline hover:text-wt-text-primary">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and protect your data.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              15. Suspension & Termination
            </h2>
            <p className="mb-4">
              We may suspend or terminate access if: you violate these Terms; your use poses security or operational risk; required by law or regulation. You may stop using the Service at any time.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              16. Changes to These Terms
            </h2>
            <p>
              We may modify these Terms from time to time. Material changes will be communicated via email or in-app notice. Continued use of the Service constitutes acceptance of updated Terms.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              17. Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the United States and the applicable state or jurisdiction of Watt Tower’s principal place of business, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="mb-4 font-sans text-xl font-semibold text-wt-text-primary">
              18. Contact Information
            </h2>
            <p className="mb-4">Watt Tower</p>
            <p>Email: support@watttower.com</p>
          </section>
        </article>

        <div className="mt-16 pt-8 border-t border-wt-border-default flex flex-wrap gap-6">
          <Link
            to="/"
            className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
          >
            ← Back to home
          </Link>
          <Link
            to="/privacy"
            className="font-sans text-sm text-wt-text-secondary hover:text-wt-text-primary transition-colors"
          >
            Privacy Policy
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
